import { Route, BrowserRouter as Router, Routes, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './styles/theme.css';
import MainView from './components/main-view/MainView';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AccountSettings from './components/account-settings/AccountSettings';
import OrganizationSettings from './components/organization-settings/OrganizationSettings';
import Documentation from './components/documentation/Documentation';
import LoadingScreen from './components/common/LoadingScreen';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useAppSelector, useAppDispatch } from './redux/store';
import { DBUserThunks, userSelectOneObjectByQuery } from './redux/thunks/DBServiceThunks';
import { useInitializeUserLanguage } from './hooks/useInitializeUserLanguage';
import organizationService from './services/organization.service';
import { setMembers } from './redux/slices/organizationSlice';

// Dev Admin Components - Only imported in development (tree-shaken in production)
const DevAdminPanel = import.meta.env.DEV ? lazy(() => import('./components/dev-admin/DevAdminPanel')) : null;
const UserDetails = import.meta.env.DEV ? lazy(() => import('./components/dev-admin/UserDetails')) : null;
const UserActivity = import.meta.env.DEV ? lazy(() => import('./components/dev-admin/UserActivity')) : null;

// Home page component
const Home = () => {
  const { t } = useTranslation();

  return (
    <div className="my-8">
      <h1 className="text-3xl font-bold text-foreground">{t('pages.home.title')}</h1>
      <p className="text-muted-foreground mt-2">{t('pages.home.welcome')}</p>
    </div>
  );
};

// Root redirect component - redirects / to /:orgSlug
const RootRedirect = () => {
  const organization = useAppSelector(state => state.organization.currentOrganization);
  const orgSlug = organization?.customSlug || organization?.slug;

  if (orgSlug) {
    return <Navigate to={`/${orgSlug}`} replace />;
  }

  // If no organization, show loading or stay on root
  return <MainView />;
};


// Main App Logic Component (inside AuthProvider context)
const AppContent = () => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();

  const organization = useAppSelector(state => state.organization.currentOrganization);

  // Initialize user language preference
  useInitializeUserLanguage();

  // Get user state from Redux (authentication)
  const { user, isAuthenticated, initialized } = useAppSelector(state => state.UserReducer);
  const isLoading = useAppSelector(state => state.DBUserReducer.itemsLoading);

  // Get database user object
  const dbUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));

  // Get authentication state from context
  const { isAuthenticated: contextAuthenticated, isSwitchingOrganization } = useAuth();




  // Determine if we're authenticated (from either source)
  const isUserAuthenticated = isAuthenticated || contextAuthenticated;

  // Simple flag to track if we've attempted to fetch database user
  const fetchAttemptedRef = useRef(false);



  // Reset fetchAttempted when user authentication state changes
  useEffect(() => {
    if (isUserAuthenticated && user?._id) {
      fetchAttemptedRef.current = false;
    } else if (!isUserAuthenticated) {
      fetchAttemptedRef.current = false;
    }
  }, [isUserAuthenticated, user?._id]);

  useEffect(() => {

    // If not initialized yet, wait
    if (!initialized) {
      return;
    }

    // If we have a user and haven't attempted fetch yet
    if (user?._id && !fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true;

      dispatch(DBUserThunks.find({ _id: user._id }));
    }
  }, [user, initialized, dispatch]);

  // Load organization members when user is authenticated
  useEffect(() => {
    const loadOrganizationMembers = async () => {
      if (dbUser && isUserAuthenticated) {
        try {
          const members = await organizationService.getMembers();
          dispatch(setMembers(members));
        } catch (err) {
          console.error('Failed to load organization members:', err);
        }
      }
    };

    loadOrganizationMembers();
  }, [dbUser, isUserAuthenticated, dispatch]);




  // Show loading screen while determining authentication state or fetching user from DB
  // Also wait for i18n to be ready to avoid missing key errors
  // CRITICAL: Show loading screen during organization switch to prevent login form flash
  if (!initialized || (isUserAuthenticated && !fetchAttemptedRef.current) || isLoading || !i18n.isInitialized || isSwitchingOrganization) {
    return <LoadingScreen />;
  }

  // If not authenticated, show public routes only
  if (!isUserAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        {/* DEV-only: preview the Help & Support docs without auth (stripped from prod builds) */}
        {import.meta.env.DEV && <Route path="/__docs_preview" element={<Documentation />} />}
        <Route path="/welcome" element={
          <div className="container max-w-7xl mx-auto px-4">
            <Home />
          </div>
        } />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  // If authenticated but no database user exists (and we've tried fetching), redirect to login
  // But only if we've actually attempted to fetch - otherwise show loading
  if (!dbUser && fetchAttemptedRef.current && !isLoading) {
    console.warn('⚠️ Authenticated but no database user found after fetch attempt, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // If we're still loading the database user, show loading screen
  if (!dbUser) {
    console.log('⏳ Waiting for database user to load...');
    return <LoadingScreen />;
  }

  // Render protected routes only when database user exists
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      {/* <Route path="/signup" element={<Signup />} /> */}
      <Route path="/welcome" element={
        <div className="container max-w-7xl mx-auto px-4">
          <Home />
        </div>
      } />

      {/* Protected routes - org-scoped URLs */}
      <Route element={<ProtectedRoute />}>
        {/* Organization home: /smith-associates */}
        <Route path="/:orgSlug" element={<MainView />} />

        {/* Case view: /smith-associates/123 */}
        <Route path="/:orgSlug/:caseId" element={<MainView />} />

        {/* Chat view: /smith-associates/123/456 */}
        <Route path="/:orgSlug/:caseId/:chatId" element={<MainView />} />

        {/* Settings: /smith-associates/account-settings */}
        <Route path="/:orgSlug/account-settings" element={<AccountSettings />} />
        <Route path="/:orgSlug/organization-settings" element={<OrganizationSettings />} />
        <Route path="/:orgSlug/help" element={<Documentation />} />

        {/* Dev Admin Routes - Only in development */}
        {import.meta.env.DEV && DevAdminPanel && UserDetails && UserActivity && (
          <>
            <Route path="/dev-admin" element={<Suspense fallback={<LoadingScreen />}><DevAdminPanel /></Suspense>} />
            <Route path="/dev-admin/users/:userId" element={<Suspense fallback={<LoadingScreen />}><UserDetails /></Suspense>} />
            <Route path="/dev-admin/users/:userId/activity" element={<Suspense fallback={<LoadingScreen />}><UserActivity /></Suspense>} />
          </>
        )}

      </Route>

      {/* Root route handler - redirect to org home if user has organization */}
      <Route path="/" element={<ProtectedRoute />}>
        <Route index element={<RootRedirect />} />
      </Route>

      {/* Redirect unknown routes to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
